import { action, computed, observable } from 'mobx';
import { PageableQueryParams } from '@/types/pagination';
import StoreConstructor from '@/store/core/StoreConstructor';

export default abstract class ListStore<T = any> extends StoreConstructor {
  @observable data?: T;
  @observable pageable: PageableQueryParams = {
    limit: 10,
    direction: 'DESC',
    page: 1,
    sortBy: '',
  };
  @observable pageSizes = [10, 20, 50, 100];
  @observable isFetching = false;
  @observable filters: Record<string, any> = {};

  sortColumns: any[] = [];

  @computed get sortState() {
    return {
      sortBy: this.pageable.sortBy,
      direction: this.pageable.direction,
      sortHandler: this.sortHandler.bind(this),
      sortColumns: this.sortColumns,
    };
  }

  @computed get notFalsyParams(): Record<string, any> {
    const notFalsyPageable = Object.entries(this.pageable).filter(
      ([_, value]) => Boolean(value),
    );
    const notFalsyFilters = Object.entries(this.filters).filter(([_, value]) =>
      Boolean(value),
    );

    return {
      ...Object.fromEntries(notFalsyFilters),
      ...Object.fromEntries(notFalsyPageable),
    };
  }

  @computed get notFalsyFilters(): Record<string, any> {
    const notFalsyFilters = Object.entries(this.filters).filter(([_, value]) =>
      Boolean(value),
    );

    return {
      ...Object.fromEntries(notFalsyFilters),
    };
  }

  @computed get currentPage() {
    return this.pageable.page || 1;
  }

  @action.bound setPage(n: number) {
    this.pageable.page = n - 1;
    this.fetch();
  }

  @action.bound setPageable<
    K extends keyof PageableQueryParams,
    V extends PageableQueryParams[K],
  >(filters: Record<K, V>) {
    (Object.entries(filters) as Array<[K, V]>).forEach(
      ([key, value]) => (this.pageable[key] = value),
    );
    this.fetch();
  }

  @action.bound nextPage() {
    this.setPage(this.currentPage + 1);
  }

  @action.bound prevPage() {
    this.setPage(this.currentPage - 1);
  }

  @action.bound setPageSize(n: number) {
    this.pageable.page = 0;
    this.pageable.limit = n;
    this.fetch();
  }

  @action sortHandler(columnName: string) {
    if (this.pageable.sortBy === columnName) {
      this.setPageable({
        direction: this.pageable.direction === 'DESC' ? 'ASC' : 'DESC',
      });
    } else {
      this.setPageable({
        sortBy: columnName,
        direction: 'DESC',
      });
    }
  }

  @action setFilter<K extends string>(filters: Record<K, unknown>) {
    Object.entries(filters).forEach(
      ([key, value]) => (this.filters[key] = value),
    );
  }

  @action resetData() {
    this.data = undefined;
  }

  fetch(...params: any): Promise<void> {
    throw new Error('Need to override the Fetch method');
  }
}
